<template>
  <ComponentHeader title="Care Plan Domains" subtitle="Configure care plan domains and their module templates. Domains represent high-level categories like Physical Health, Mental Health, and Functional Status.">
    <button
      @click="createNewDomain"
      class="btn-primary-sm"
    >
      <FontAwesomeIcon :icon="['fas', 'plus']" />
      Add Domain
    </button>
  </ComponentHeader>

  <div class="care-plan-admin p-4">
    <!-- Loading State -->
    <div v-if="loading" class="py-12 text-center">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-primary-600 text-3xl animate-spin" />
    </div>

    <!-- Domains List -->
    <div v-else-if="domains.length === 0" class="py-12 text-center bg-gray-50 rounded-lg">
      <FontAwesomeIcon :icon="['fas', 'layer-group']" class="text-gray-400 text-5xl mb-3" />
      <p class="text-gray-600">No care plan domains configured</p>
    </div>

    <div v-else class="space-y-4">
      <!-- Domain Card -->
      <div
        v-for="domain in domains"
        :key="domain.id"
        class="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow"
      >
        <!-- Domain Header -->
        <div class="px-6 py-4">
          <div class="flex items-center justify-between">
            <div>
              <h5 class="font-semibold text-gray-900">{{ domain.name }}</h5>
              <p class="text-sm text-gray-500">
                ID: {{ domain.id }} • Order: {{ domain.display_order || 0 }}
              </p>
            </div>
            <div class="flex items-center space-x-2">
              <!-- Edit Entire ThreadTemplate JSON -->
              <button
                @click="editDomainJson(domain)"
                class="px-3 py-2 text-sm text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                title="Edit Full Domain Configuration"
              >
                <FontAwesomeIcon :icon="['fas', 'code']" class="mr-1" />
                Edit Full JSON
              </button>

              <!-- Delete Domain -->
              <button
                @click="deleteDomain(domain)"
                class="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete Domain"
              >
                <FontAwesomeIcon :icon="['fas', 'trash']" />
              </button>
            </div>
          </div>
        </div>

        <!-- Module Templates Section -->
        <div class="border-t px-6 py-4 bg-gray-50">
          <div class="flex items-center justify-between mb-3">
            <h6 class="font-medium text-gray-700">Module Templates</h6>
            <button
              @click="addModuleTemplate(domain)"
              class="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              <FontAwesomeIcon :icon="['fas', 'plus']" class="mr-1" />
              Add Template
            </button>
          </div>

          <!-- Templates List -->
          <div v-if="!getModuleTemplates(domain).length" class="text-center py-4 text-sm text-gray-500">
            No module templates defined
          </div>

          <div v-else class="space-y-2">
            <div
              v-for="(template, index) in getModuleTemplates(domain)"
              :key="template.template_id || index"
              class="bg-white rounded-lg border border-gray-200 p-3 flex items-center justify-between"
            >
              <div>
                <h6 class="font-medium text-gray-900">{{ template.name || `Template ${index + 1}` }}</h6>
                <p v-if="template.description" class="text-sm text-gray-600">{{ template.description }}</p>
              </div>
              <div class="flex items-center space-x-2">
                <button
                  @click="editModuleTemplate(domain, template, index)"
                  class="px-2 py-1 text-xs text-primary-600 bg-primary-50 rounded hover:bg-primary-100"
                >
                  <FontAwesomeIcon :icon="['fas', 'pen-to-square']" class="mr-1" />
                  Edit
                </button>
                <button
                  @click="deleteModuleTemplate(domain, index)"
                  class="px-2 py-1 text-xs text-red-600 bg-red-50 rounded hover:bg-red-100"
                >
                  <FontAwesomeIcon :icon="['fas', 'trash']" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- JSON Editor Modal -->
    <JSON_Editor
      v-if="showJsonEditor"
      :initial-data="editingData"
      @save="saveJsonChanges"
      @close="closeJsonEditor"
    />
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import JSON_Editor from '@/components/widgetcomponents/JSON_Editors/JSON_Editor.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';

export default {
  name: 'CarePlan_Admin',

  components: {
    FontAwesomeIcon,
    JSON_Editor,
    ComponentHeader
  },

  data() {
    return {
      domains: [],
      loading: true,

      // JSON Editor state
      showJsonEditor: false,
      editingData: null,
      editingContext: null // { type: 'domain' | 'template', domain, template?, index? }
    };
  },

  mounted() {
    this.fetchDomains();
  },

  methods: {
    async fetchDomains() {
      this.loading = true;
      try {
        // UPDATED: Use CarePlan-specific endpoint
        const response = await this.$axios.get('/api/careplans/thread-types/');
        this.domains = response.data.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      } catch (error) {
        console.error('Error fetching domains:', error);
        this.$emit('show-message', 'Failed to load care plan domains', 'error');
      } finally {
        this.loading = false;
      }
    },

    getModuleTemplates(domain) {
      return domain.metadata?.module_templates || [];
    },

    // Create new domain
    createNewDomain() {
      const defaultMetadata = {
        domain_config: {
          domain_name: 'New Domain',
          domain_color: '#6366F1',
          domain_icon: 'layer-group',
          allowed_statuses: ['planned', 'active', 'on_hold', 'completed', 'discontinued']
        },
        fields: {
          required: {},
          optional: {}
        },
        module_templates: []
      };

      this.editingData = {
        name: 'New Domain',
        description: '',
        display_order: 0,
        metadata: defaultMetadata
      };

      this.editingContext = { type: 'new_domain' };
      this.showJsonEditor = true;
    },

    // Edit entire domain JSON
    editDomainJson(domain) {
      this.editingData = {
        id: domain.id,
        name: domain.name,
        description: domain.description || '',
        display_order: domain.display_order || 0,
        metadata: domain.metadata || {}
      };

      this.editingContext = { type: 'domain', domain };
      this.showJsonEditor = true;
    },

    // Add new module template
    addModuleTemplate(domain) {
      const newTemplate = {
        template_id: `template_${Date.now()}`,
        name: 'New Module Template',
        description: '',
        fields: [],
        default_goals: [],
        default_interventions: [],
        typical_duration_weeks: null
      };

      this.editingData = newTemplate;
      this.editingContext = { type: 'new_template', domain };
      this.showJsonEditor = true;
    },

    // Edit module template
    editModuleTemplate(domain, template, index) {
      this.editingData = { ...template };
      this.editingContext = { type: 'template', domain, template, index };
      this.showJsonEditor = true;
    },

    // Delete module template
    async deleteModuleTemplate(domain, index) {
      if (!confirm('Are you sure you want to delete this template?')) {
        return;
      }

      try {
        const metadata = { ...domain.metadata };
        metadata.module_templates.splice(index, 1);

        // UPDATED: Use CarePlanThreadTemplate endpoint
        await this.$axios.patch(`/api/careplanthreadtemplates/${domain.id}/`, {
          metadata: metadata
        });

        this.$emit('show-message', 'Template deleted successfully', 'success');
        this.fetchDomains();
      } catch (error) {
        console.error('Error deleting template:', error);
        this.$emit('show-message', 'Failed to delete template', 'error');
      }
    },

    // Delete domain
    async deleteDomain(domain) {
      if (!confirm(`Are you sure you want to delete the domain "${domain.name}"?`)) {
        return;
      }

      try {
        // UPDATED: Use CarePlanThreadTemplate endpoint
        await this.$axios.delete(`/api/careplanthreadtemplates/${domain.id}/`);
        this.$emit('show-message', 'Domain deleted successfully', 'success');
        this.fetchDomains();
      } catch (error) {
        console.error('Error deleting domain:', error);
        this.$emit('show-message', 'Failed to delete domain', 'error');
      }
    },

    // Save JSON changes
    async saveJsonChanges(data) {
      try {
        const context = this.editingContext;

        if (context.type === 'new_domain') {
          // UPDATED: Use CarePlanThreadTemplate endpoint
          await this.$axios.post('/api/careplanthreadtemplates/', data);
          this.$emit('show-message', 'Domain created successfully', 'success');
        }
        else if (context.type === 'domain') {
          // UPDATED: Use CarePlanThreadTemplate endpoint
          await this.$axios.patch(`/api/careplanthreadtemplates/${context.domain.id}/`, data);
          this.$emit('show-message', 'Domain updated successfully', 'success');
        }
        else if (context.type === 'new_template') {
          // Add new template to domain
          const metadata = { ...context.domain.metadata };
          if (!metadata.module_templates) {
            metadata.module_templates = [];
          }
          metadata.module_templates.push(data);

          // UPDATED: Use CarePlanThreadTemplate endpoint
          await this.$axios.patch(`/api/careplanthreadtemplates/${context.domain.id}/`, {
            metadata: metadata
          });
          this.$emit('show-message', 'Template added successfully', 'success');
        }
        else if (context.type === 'template') {
          // Update existing template
          const metadata = { ...context.domain.metadata };
          metadata.module_templates[context.index] = data;

          // UPDATED: Use CarePlanThreadTemplate endpoint
          await this.$axios.patch(`/api/careplanthreadtemplates/${context.domain.id}/`, {
            metadata: metadata
          });
          this.$emit('show-message', 'Template updated successfully', 'success');
        }

        this.closeJsonEditor();
        this.fetchDomains();
      } catch (error) {
        console.error('Error saving changes:', error);
        this.$emit('show-message', 'Failed to save changes', 'error');
      }
    },

    // Close JSON editor
    closeJsonEditor() {
      this.showJsonEditor = false;
      this.editingData = null;
      this.editingContext = null;
    }
  }
};
</script>

<style scoped>
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
